/**
 * Category matcher.
 *
 * Learns from your already-categorised transaction history and predicts a
 * category for incoming rows. There is no separate rules table to maintain —
 * your history IS the training data, so corrections you make in the review
 * screen automatically improve the next import.
 *
 * Matching runs in tiers, most reliable first:
 *   1. exact  — identical normalised description
 *   2. high   — same merchant key (merchant + account/store discriminators)
 *   3. medium — token-overlap similarity above MEDIUM_THRESHOLD
 *   4. none   — falls through to manual mapping
 *
 * IMPORTANT — account numbers are preserved deliberately.
 * ING writes every internal transfer as "Internal Transfer - Internal Transfer
 * - Receipt <n> Savings Maximiser <account>". The receipt number is noise, but
 * the trailing account number is the ONLY thing distinguishing eleven different
 * savings accounts that map to eleven different categories. Stripping long
 * digit runs (a tempting way to remove noise) collapses ~2,500 transactions
 * into a single bucket and silently miscategorises all of them.
 */

const MEDIUM_THRESHOLD = 0.62;   // Dice coefficient floor for a fuzzy match
const MIN_SAMPLES_FOR_HIGH = 1;  // occurrences needed before a key is trusted

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Strip the parts of a description that change between otherwise-identical
 * transactions: receipt numbers, embedded dates, masked card numbers.
 */
export function normalise(desc) {
  let s = (desc || '').toLowerCase();

  s = s.replace(/receipt\s*\d+\s*(in)?/g, ' ');                 // "Receipt 110324In"
  s = s.replace(/\bdate\s+\d{1,2}\s+\w{3}\s+\d{4}\b/g, ' ');    // "Date 27 Jan 2024"
  s = s.replace(/\bcard\s+xx\d+/g, ' ');                        // "Card xx1335"
  s = s.replace(/\b\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}\b/g, ' '); // bare dates
  s = s.replace(/\b(pty|ltd|australia)\b/g, ' ');               // corporate noise
  s = s.replace(/[^a-z0-9\s]/g, ' ');                           // punctuation
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}

/**
 * A stable identity for a merchant or transfer destination.
 * Keeps the first few words PLUS any long digit runs, because those digit runs
 * are account / store identifiers that carry real meaning (see file header).
 */
export function merchantKey(desc) {
  const n = normalise(desc);
  const tokens = n.split(' ').filter(t => t.length > 2);
  const words = tokens.filter(t => !/^\d+$/.test(t)).slice(0, 4);
  const identifiers = tokens.filter(t => /^\d{6,}$/.test(t));
  return [...words, ...identifiers].join(' ');
}

/** Token set for fuzzy comparison. */
function tokenSet(desc) {
  return new Set(normalise(desc).split(' ').filter(t => t.length > 2));
}

/** Dice coefficient — 0 (nothing shared) to 1 (identical token sets). */
function dice(a, b) {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const t of a) if (b.has(t)) shared++;
  return (2 * shared) / (a.size + b.size);
}

// ---------------------------------------------------------------------------
// Index building
// ---------------------------------------------------------------------------

/**
 * Build lookup indexes from categorised history.
 * Where one key has been categorised inconsistently, the most frequent
 * category wins and the runners-up are surfaced as alternatives.
 */
export function buildIndex(historicalTransactions) {
  const exact = new Map();     // normalised description -> {counts}
  const byMerchant = new Map(); // merchant key          -> {counts}

  const bump = (map, key, category) => {
    if (!key || !category) return;
    if (!map.has(key)) map.set(key, new Map());
    const counts = map.get(key);
    counts.set(category, (counts.get(category) || 0) + 1);
  };

  for (const t of historicalTransactions) {
    if (!t.category) continue;
    bump(exact, normalise(t.description), t.category);
    bump(byMerchant, merchantKey(t.description), t.category);
  }

  // Fuzzy tier needs the raw token sets, but only for merchant keys we know.
  const fuzzyCandidates = [...byMerchant.keys()].map(key => ({
    key,
    tokens: tokenSet(key),
  }));

  return { exact, byMerchant, fuzzyCandidates };
}

/** Pick the winning category plus any alternatives, sorted by frequency. */
function resolve(counts) {
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((s, [, n]) => s + n, 0);
  return {
    category: sorted[0][0],
    samples: sorted[0][1],
    // Anything that isn't the winner is offered as a one-click alternative,
    // so an inconsistently-categorised merchant is easy to correct.
    alternatives: sorted.slice(1).map(([c]) => c),
    agreement: sorted[0][1] / total,
  };
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/**
 * Predict a category for one transaction.
 * Returns { category, confidence, reason, alternatives, samples }.
 * confidence is one of: 'exact' | 'high' | 'medium' | 'none'
 */
export function matchOne(transaction, index) {
  const desc = transaction.description;

  // The parser may have already assigned a category (e.g. ANZ card repayments,
  // or a Category column in a generic CSV). Trust it and skip matching.
  if (transaction.category) {
    return {
      category: transaction.category,
      confidence: 'exact',
      reason: 'From file',
      alternatives: [],
      samples: 0,
    };
  }

  // Tier 1 — exact normalised description
  const exactHit = index.exact.get(normalise(desc));
  if (exactHit) {
    const r = resolve(exactHit);
    return {
      ...r,
      confidence: 'exact',
      reason: `Exact match · ${r.samples} previous`,
    };
  }

  // Tier 2 — merchant key
  const key = merchantKey(desc);
  const merchantHit = index.byMerchant.get(key);
  if (merchantHit) {
    const r = resolve(merchantHit);
    if (r.samples >= MIN_SAMPLES_FOR_HIGH) {
      return {
        ...r,
        confidence: 'high',
        reason: `Merchant match · ${r.samples} previous`,
      };
    }
  }

  // Tier 3 — fuzzy token overlap against known merchant keys
  const tokens = tokenSet(desc);
  let best = null;
  for (const cand of index.fuzzyCandidates) {
    const score = dice(tokens, cand.tokens);
    if (score >= MEDIUM_THRESHOLD && (!best || score > best.score)) {
      best = { score, key: cand.key };
    }
  }
  if (best) {
    const r = resolve(index.byMerchant.get(best.key));
    return {
      ...r,
      confidence: 'medium',
      reason: `Similar to "${best.key}" · ${Math.round(best.score * 100)}% match`,
    };
  }

  // Tier 4 — no match
  return {
    category: '',
    confidence: 'none',
    reason: 'No match found',
    alternatives: [],
    samples: 0,
  };
}

/** Run the matcher across a batch. */
export function matchAll(transactions, historicalTransactions) {
  const index = buildIndex(historicalTransactions);
  return transactions.map(t => ({ ...t, match: matchOne(t, index) }));
}

/** Summary counts for the review screen header. */
export function summarise(matched) {
  const counts = { exact: 0, high: 0, medium: 0, none: 0 };
  for (const t of matched) counts[t.match.confidence]++;
  return counts;
}
